import React from "react";
import { format } from "date-fns";
import { useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/styles";

import { Drawer, ListItem, ListItemSecondaryAction, ListItemText, List as MuiList, Divider, Typography } from "@material-ui/core";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";

import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import SyncIcon from "@material-ui/icons/Sync";

import { Button, showNotification } from "jazasoft";

import { isEmpty, syntaxHighlight } from "../../../utils/helpers";
import { useDeepMemo, useMutation, useSafeState } from "../../../hooks";
import hasPrivilege from "../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../utils/privilege";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flex: 1,
    flexDirection: "column"
  },
  drawerPaper: {
    maxWidth: 1000,
    minWidth: 600
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  },
  content: {
    flex: 1
  }
}));

const AuditDetailsDrawer = ({ open, fabricRelease, roles, hasAccess, onDrawerClose }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useSafeState(false);

  const [mutate, { loading: busy }] = useMutation();
  const dispatch = useDispatch();

  const externalIds = !isEmpty(fabricRelease?.externalIds) ? JSON.parse(fabricRelease.externalIds) : [];
  const externalId = externalIds[0];

  const listData = useDeepMemo(() => {
    if (isEmpty(externalId)) return [];
    let listData = [
      { label: "External App", value: externalId.appId || "" },
      { label: "Last Sync At", value: !isEmpty(externalId.lastSyncAt) ? format(externalId.lastSyncAt, "MMM dd, yy hh:mm a") : "-" }
    ];

    return listData;
  }, [externalId]);

  const jsonData = useDeepMemo(() => {
    if (isEmpty(externalId)) return [];
    let jsonData = [
      { label: "Error Message", value: externalId.errorMessage || "", isJson: false },
      { label: "Error", value: externalId.error || "", isJson: true }
    ];
    return jsonData.filter(e => !isEmpty(e.value));
  }, [externalId]);

  const onSyncClick = () => {
    const options = {
      url: `fabric-release/${fabricRelease?.id}/sync`,
      method: "PATCH"
    };
    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("Manual Sync initiated successfully. Fabric Release will be synced in few minutes."));
        onDrawerClose && onDrawerClose();
      }
    });
  };

  const isSynced = externalId?.syncedVersion > 0 && isEmpty(externalId?.error) && isEmpty(externalId?.errorMessage);

  return (
    <Drawer classes={{ paper: classes.drawerPaper }} anchor="right" open={open} onClose={onDrawerClose} style={{ flex: 1 }}>
      <div className={classes.root}>
        {/** Drawer Header */}
        <div className={classes.drawerHeader}>
          <div style={{ padding: "0.5rem" }}>
            <IconButton onClick={onDrawerClose}>
              <ChevronRightIcon />
            </IconButton>
          </div>

          <div style={{ display: "flex", flex: 1, flexDirection: "row", justifyContent: "space-between" }}>
            <Typography variant="h5">TXN: {fabricRelease?.txn}</Typography>
            {hasPrivilege(roles, hasAccess, ResourceId.FABRIC_RELEASE, Scope.WRITE) && (
              <Button label="Sync" variant="contained" color="primary" disabled={isSynced || busy} onClick={onSyncClick}>
                <SyncIcon />
              </Button>
            )}
          </div>

          {/* <div style={{ paddingHorizontal: 10, width: "100%" }}>
            <Typography style={{ fontWeight: "bold", fontSize: 16 }}>TXN: {fabricRelease?.txn}</Typography>
          </div> */}
        </div>
        <Divider />

        {/** Drawer Content **/}
        <div className={classes.content}>
          <MuiList disablePadding>
            {listData.map(({ label, value }, idx) => (
              <ListItem key={idx} divider>
                <ListItemText primary={label} />
                <ListItemSecondaryAction>
                  <ListItemText primary={value} />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </MuiList>
          {jsonData.map((data, idx) => (
            <Accordion key={idx} expanded={expanded === data.label} onChange={(e, isExpanded) => setExpanded(isExpanded ? data.label : false)}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{data.label}</Typography>
              </AccordionSummary>
              <Divider />
              <AccordionDetails>
                <Divider />
                <div>
                  {data.isJson && (
                    <span
                      style={{ whiteSpace: "pre", fontSize: 14 }}
                      dangerouslySetInnerHTML={{ __html: syntaxHighlight(JSON.stringify(JSON.parse(data.value), null, 4)) }}
                    />
                  )}
                  {!data.isJson && <Typography>{data.value}</Typography>}
                </div>
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      </div>
    </Drawer>
  );
};

export default AuditDetailsDrawer;
